// @@TASK_LISTS_v100@@ One to-do: cross off, assign many, move between lists,
// notes, and a comment thread. Every field saves on change.
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { AssigneePicker, Avatar, CheckCircle, fmtDue, fmtWhen, dueTone, STATUS_LABEL } from '../components/TodoBits';

const JSONH = { 'Content-Type': 'application/json' };
const PRIORITIES = ['low', 'normal', 'high', 'urgent'];
const RECURRENCES = ['none', 'daily', 'weekly', 'monthly'];

function recurLabel(t) {
  if (!t || t.recurrence === 'none') return 'Does not repeat';
  const n = t.recurrence_interval || 1;
  const unit = { daily: 'day', weekly: 'week', monthly: 'month' }[t.recurrence];
  return n === 1 ? `Every ${unit}` : `Every ${n} ${unit}s`;
}

export default function TaskDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const [task, setTask] = useState(null);
  const [comments, setComments] = useState([]);
  const [groups, setGroups] = useState([]);
  const [people, setPeople] = useState([]);
  const [clients, setClients] = useState([]);
  const [lists, setLists] = useState([]);
  const [msg, setMsg] = useState('');
  const [titleEdit, setTitleEdit] = useState(null);
  const [notes, setNotes] = useState(null);
  const [draft, setDraft] = useState('');
  const [editC, setEditC] = useState(null);

  const load = useCallback(async () => {
    const res = await apiFetch(`/api/tasks/${id}`);
    if (res.ok) { const d = await res.json(); setTask(d.task); setComments(d.comments || []); setGroups(d.groups || []); }
    else if (res.status === 404) setMsg('This to-do doesn’t exist (it may have been deleted).');
  }, [id]);
  useEffect(() => { setTask(null); setMsg(''); setNotes(null); setTitleEdit(null); load(); }, [load]);
  useEffect(() => {
    Promise.all([apiFetch('/api/tasks/assignees'), apiFetch('/api/clients'), apiFetch('/api/tasks/lists')]).then(async ([a, c, l]) => {
      if (a.ok) setPeople((await a.json()).assignees || []);
      if (c.ok) setClients((await c.json()).clients || []);
      if (l.ok) setLists((await l.json()).lists || []);
    }).catch(() => {});
  }, []);

  async function patch(body) {
    setMsg('');
    const res = await apiFetch(`/api/tasks/${id}`, { method: 'PATCH', headers: JSONH, body: JSON.stringify(body) });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) { setMsg(d.error || 'Could not save.'); return null; }
    setTask(d.task);
    if (d.spawned) setMsg(`Done. The next one is scheduled for ${fmtDue(d.spawned.due_date) || 'later'}.`);
    if (body.list_id !== undefined || body.group_id !== undefined) load();
    return d;
  }
  async function remove() {
    if (!window.confirm('Delete this to-do and its comments? This cannot be undone.')) return;
    const res = await apiFetch(`/api/tasks/${id}`, { method: 'DELETE' });
    if (res.ok) navigate(`/tasks/lists/${task.list_id || 'inbox'}`);
  }
  async function addComment(e) {
    if (e) e.preventDefault();
    if (!draft.trim()) return;
    const res = await apiFetch(`/api/tasks/${id}/comments`, { method: 'POST', headers: JSONH, body: JSON.stringify({ body: draft }) });
    if (res.ok) { const d = await res.json(); setComments((c) => [...c, d.comment]); setDraft(''); }
  }
  async function saveComment(e) {
    e.preventDefault();
    const res = await apiFetch(`/api/tasks/${id}/comments/${editC.id}`, { method: 'PATCH', headers: JSONH, body: JSON.stringify({ body: editC.body }) });
    if (res.ok) { const d = await res.json(); setComments((cs) => cs.map((c) => (c.id === editC.id ? { ...c, ...d.comment } : c))); setEditC(null); }
  }
  async function deleteComment(c) {
    if (!window.confirm('Delete this comment?')) return;
    const res = await apiFetch(`/api/tasks/${id}/comments/${c.id}`, { method: 'DELETE' });
    if (res.ok) setComments((cs) => cs.filter((x) => x.id !== c.id));
  }

  if (msg && !task) return <div className="hq-page-content"><div className="hq-page-head"><h1>Tasks</h1><p>{msg}</p></div><Link to="/tasks" className="hq-todo-linkbtn">← All lists</Link></div>;
  if (!task) return <div className="hq-page-content"><p className="hq-empty">Loading…</p></div>;

  const done = task.status === 'done';
  const closed = done || task.status === 'cancelled';
  const listKey = task.list_id || 'inbox';
  const listChoices = lists.some((l) => l.id === task.list_id) || !task.list_id ? lists : [...lists, { id: task.list_id, name: task.list_name + ' (archived)' }];
  const isMod = user && ['admin', 'manager'].includes(user.role);

  return (
    <div className="hq-page-content hq-todo-page">
      <div className="hq-todo-crumb">
        <Link to="/tasks">Tasks</Link> <span>›</span> <Link to={`/tasks/lists/${listKey}`}>{task.list_name || 'Inbox'}</Link>
        {task.group_name && <><span>›</span> {task.group_name}</>}
      </div>

      <div className="hq-panel hq-todo-detail">
        <div className={'hq-todo-detail-head' + (closed ? ' is-done' : '')}>
          <CheckCircle checked={closed} onClick={() => patch({ status: done ? 'todo' : 'done' })} label={done ? 'Mark not done' : 'Mark done'} />
          {titleEdit !== null ? (
            <form className="hq-todo-inlineform hq-todo-titleform" onSubmit={async (e) => { e.preventDefault(); if (await patch({ title: titleEdit })) setTitleEdit(null); }}>
              <input className="hq-input" autoFocus value={titleEdit} onChange={(e) => setTitleEdit(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Escape') setTitleEdit(null); }} />
              <button className="hq-btn" type="submit" disabled={!titleEdit.trim()}>Save</button>
            </form>
          ) : (
            <h1 className="hq-todo-detail-title" title="Click to edit" onClick={() => setTitleEdit(task.title || '')}>{task.title || '(untitled)'}</h1>
          )}
        </div>
        {task.completed_at && done && (
          <p className="hq-todo-donebanner">✓ Completed{task.completed_by_name ? ` by ${task.completed_by_name}` : ''} · {fmtWhen(task.completed_at)}</p>
        )}
        {msg && <p className="hq-msg hq-todo-msg">{msg}</p>}

        <dl className="hq-todo-fields">
          <dt>Assigned to</dt>
          <dd><AssigneePicker people={people} value={(task.assignees || []).map((p) => p.id)} onChange={(v) => patch({ assignee_ids: v })} /></dd>

          <dt>Due on</dt>
          <dd className="hq-todo-fieldrow">
            <input className="hq-input hq-select-sm" type="date" value={task.due_date ? String(task.due_date).slice(0, 10) : ''}
              onChange={(e) => patch({ due_date: e.target.value || null })} />
            {task.due_date && <span className={'hq-todo-due ' + dueTone(task.due_date, task.status)}>{fmtDue(task.due_date)}</span>}
          </dd>

          <dt>List</dt>
          <dd className="hq-todo-fieldrow">
            <select className="hq-select hq-select-sm" value={task.list_id || ''} onChange={(e) => patch({ list_id: e.target.value || 'inbox' })}>
              <option value="">Inbox</option>
              {listChoices.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
            {task.list_id && (
              <select className="hq-select hq-select-sm" value={task.group_id || ''} onChange={(e) => patch({ group_id: e.target.value || null })} aria-label="Group">
                <option value="">No group</option>
                {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            )}
          </dd>

          <dt>Client</dt>
          <dd>
            <select className="hq-select hq-select-sm" value={task.client_id || ''} onChange={(e) => patch({ client_id: e.target.value })}>
              <option value="">No client</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name || '(unnamed)'}</option>)}
            </select>
          </dd>

          <dt>Status</dt>
          <dd className="hq-todo-fieldrow">
            <select className="hq-select hq-select-sm" value={task.status} onChange={(e) => patch({ status: e.target.value })}>
              {Object.keys(STATUS_LABEL).map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
            </select>
            <select className="hq-select hq-select-sm" value={task.priority} onChange={(e) => patch({ priority: e.target.value })} aria-label="Priority">
              {PRIORITIES.map((p) => <option key={p} value={p}>{p === 'normal' ? 'Normal priority' : p[0].toUpperCase() + p.slice(1) + ' priority'}</option>)}
            </select>
          </dd>

          <dt>Repeats</dt>
          <dd className="hq-todo-fieldrow">
            <select className="hq-select hq-select-sm" value={task.recurrence} onChange={(e) => patch({ recurrence: e.target.value })}>
              {RECURRENCES.map((r) => <option key={r} value={r}>{r === 'none' ? 'Does not repeat' : r[0].toUpperCase() + r.slice(1)}</option>)}
            </select>
            {task.recurrence !== 'none' && (
              <label className="hq-todo-inline-label">every
                <input className="hq-input hq-select-sm" type="number" min={1} max={365} style={{ width: 70 }} defaultValue={task.recurrence_interval}
                  key={task.recurrence_interval}
                  onBlur={(e) => { const n = Number(e.target.value); if (n !== task.recurrence_interval) patch({ recurrence_interval: n }); }} />
              </label>
            )}
            <small className="hq-todo-note">{task.recurrence !== 'none' ? `${recurLabel(task)} — checking it off creates the next one.` : ''}</small>
          </dd>

          <dt>Notes</dt>
          <dd>
            {notes !== null ? (
              <form onSubmit={async (e) => { e.preventDefault(); if (await patch({ description: notes })) setNotes(null); }}>
                <textarea className="hq-input hq-todo-textarea" rows={5} autoFocus value={notes} onChange={(e) => setNotes(e.target.value)} />
                <div className="hq-todo-actions">
                  <button className="hq-btn" type="submit">Save notes</button>
                  <button className="hq-btn hq-btn-ghost" type="button" onClick={() => setNotes(null)}>Cancel</button>
                </div>
              </form>
            ) : (
              <div className={'hq-todo-notes' + (task.description ? '' : ' is-empty')} onClick={() => setNotes(task.description || '')} title="Click to edit">
                {task.description || 'Add extra details…'}
              </div>
            )}
          </dd>
          {task.parent_task_id && (
            <>
              <dt>History</dt>
              <dd><Link to={`/tasks/${task.parent_task_id}`} className="hq-todo-linkbtn">View the previous occurrence</Link></dd>
            </>
          )}
        </dl>
        <p className="hq-todo-created">Added {fmtWhen(task.created_at)}</p>
      </div>

      <div className="hq-panel hq-todo-comments">
        <div className="hq-panel-head"><h2>Comments{comments.length ? ` (${comments.length})` : ''}</h2></div>
        <div className="hq-todo-thread">
          {comments.map((c) => {
            const mineC = user && c.author_id === user.id;
            return (
              <div key={c.id} className="hq-todo-comment">
                <Avatar person={{ id: c.author_id, name: c.author_name }} size={30} />
                <div className="hq-todo-comment-body">
                  <div className="hq-todo-comment-meta">
                    <strong>{c.author_name}</strong> <span>{fmtWhen(c.created_at)}{c.updated_at && new Date(c.updated_at) - new Date(c.created_at) > 1000 ? ' · edited' : ''}</span>
                    {(mineC || isMod) && editC?.id !== c.id && (
                      <span className="hq-todo-groupacts">
                        {mineC && <button className="hq-todo-linkbtn" onClick={() => setEditC({ id: c.id, body: c.body })}>Edit</button>}
                        <button className="hq-todo-linkbtn hq-todo-linkbtn-danger" onClick={() => deleteComment(c)}>Delete</button>
                      </span>
                    )}
                  </div>
                  {editC && editC.id === c.id ? (
                    <form onSubmit={saveComment}>
                      <textarea className="hq-input hq-todo-textarea" rows={3} autoFocus value={editC.body} onChange={(e) => setEditC({ ...editC, body: e.target.value })} />
                      <div className="hq-todo-actions">
                        <button className="hq-btn" type="submit" disabled={!editC.body.trim()}>Save</button>
                        <button className="hq-btn hq-btn-ghost" type="button" onClick={() => setEditC(null)}>Cancel</button>
                      </div>
                    </form>
                  ) : <p>{c.body}</p>}
                </div>
              </div>
            );
          })}
          <form className="hq-todo-comment hq-todo-comment-new" onSubmit={addComment}>
            {user && <Avatar person={{ id: user.id, name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email }} size={30} />}
            <div className="hq-todo-comment-body">
              <textarea className="hq-input hq-todo-textarea" rows={2} placeholder="Add a comment…" value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) addComment(); }} />
              {draft.trim() && (
                <div className="hq-todo-actions">
                  <button className="hq-btn" type="submit">Add this comment</button>
                  <small className="hq-todo-note">⌘ + Enter</small>
                </div>
              )}
            </div>
          </form>
        </div>
      </div>

      <div className="hq-todo-actions">
        <button className="hq-btn hq-btn-ghost" onClick={() => navigate(`/tasks/lists/${listKey}`)}>← Back to {task.list_name || 'Inbox'}</button>
        <button className="hq-link-danger" onClick={remove}>Delete to-do</button>
      </div>
    </div>
  );
}
